import { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";
import type { GeneratedTag, GeneratedMapping } from "../context/SMMGeneratorContext";
import { TagReviewListPanel, type TagReviewFilter } from "./TagReviewListPanel";
import { TagReviewDetailPanel, type TagReviewEditDraft } from "./TagReviewDetailPanel";

export const TagReviewDialog = () => {
  const {
    tagReviewOpen,
    setTagReviewOpen,
    generatedTags,
    generatedMappings,
    handleSaveTags,
    isSavingTags,
  } = useSMMGeneratorContext();

  const [tags, setTags] = useState<GeneratedTag[]>(generatedTags);
  const [mappings, setMappings] = useState<GeneratedMapping[]>(generatedMappings);
  const [filter, setFilter] = useState<TagReviewFilter>("all");
  const [selectedGlobalIndex, setSelectedGlobalIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<TagReviewEditDraft | null>(null);

  const tagsRef = useRef(tags);
  const mappingsRef = useRef(mappings);
  tagsRef.current = tags;
  mappingsRef.current = mappings;

  useEffect(() => {
    setTags(generatedTags);
  }, [generatedTags]);

  useEffect(() => {
    setMappings(generatedMappings);
  }, [generatedMappings]);

  const filteredIndices = useMemo(() => {
    return tags
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => filter === "all" || t.type === filter)
      .map(({ i }) => i);
  }, [tags, filter]);

  useEffect(() => {
    if (selectedGlobalIndex == null) return;
    if (selectedGlobalIndex >= tags.length) {
      setSelectedGlobalIndex(null);
      return;
    }
    if (!filteredIndices.includes(selectedGlobalIndex)) {
      setSelectedGlobalIndex(null);
    }
  }, [filteredIndices, selectedGlobalIndex, tags.length]);

  useEffect(() => {
    if (!tagReviewOpen) {
      setFilter("all");
      setSelectedGlobalIndex(null);
      setEditDraft(null);
    }
  }, [tagReviewOpen]);

  useLayoutEffect(() => {
    const curTags = tagsRef.current;
    const curMappings = mappingsRef.current;
    if (selectedGlobalIndex == null || selectedGlobalIndex >= curTags.length) {
      setEditDraft(null);
      return;
    }
    const t = curTags[selectedGlobalIndex];
    if (!t) {
      setEditDraft(null);
      return;
    }
    const linked =
      t.type === "solution"
        ? curMappings.find((m) => m.solution_label === t.label)?.problem_label ?? ""
        : "";
    setEditDraft({
      label: t.label,
      description: t.description ?? "",
      linkedProblemLabel: linked,
    });
  }, [selectedGlobalIndex]);

  const problemOptions = useMemo(
    () =>
      tags
        .filter((t) => t.type === "problem")
        .map((t) => ({ value: t.label, label: t.label })),
    [tags],
  );

  const selectedTag = selectedGlobalIndex != null ? tags[selectedGlobalIndex] ?? null : null;

  const showLinkedProblem =
    Boolean(selectedTag?.type === "solution" && problemOptions.length > 0);

  const hasEmptyLabels = useMemo(() => tags.some((t) => !t.label.trim()), [tags]);
  const hasMissingSolutionLinks = useMemo(() => {
    const solutions = tags.filter((t) => t.type === "solution");
    if (solutions.length === 0) return false;
    const problems = tags.filter((t) => t.type === "problem");
    if (problems.length === 0) return false;
    return solutions.some((s) => !mappings.some((m) => m.solution_label === s.label));
  }, [tags, mappings]);

  const invalidSolutionIndices = useMemo(() => {
    const hasProblemTags = tags.some((t) => t.type === "problem");
    if (!hasProblemTags) return new Set<number>();
    const invalid = new Set<number>();
    tags.forEach((t, i) => {
      if (t.type === "solution" && !mappings.some((m) => m.solution_label === t.label)) {
        invalid.add(i);
      }
    });
    return invalid;
  }, [tags, mappings]);

  const handleRemove = useCallback(
    (globalIndex: number) => {
      const removedTag = tags[globalIndex];
      if (!removedTag) return;

      setTags((prev) => prev.filter((_, i) => i !== globalIndex));
      setMappings((prev) =>
        prev.filter((m) => m.problem_label !== removedTag.label && m.solution_label !== removedTag.label),
      );

      setSelectedGlobalIndex((sel) => {
        if (sel === globalIndex) return null;
        if (sel != null && sel > globalIndex) return sel - 1;
        return sel;
      });
    },
    [tags],
  );

  const handleSave = () => {
    handleSaveTags(tags, mappings);
  };

  const onDraftChange = useCallback((patch: Partial<TagReviewEditDraft>) => {
    setEditDraft((d) => (d ? { ...d, ...patch } : d));

    const idx = selectedGlobalIndex;
    if (idx == null) return;
    const currentTag = tagsRef.current[idx];
    if (!currentTag) return;

    if (patch.label !== undefined || patch.description !== undefined) {
      const oldLabel = currentTag.label;
      setTags((prev) => {
        const t = prev[idx];
        if (!t) return prev;
        const n = [...prev];
        n[idx] = {
          ...t,
          ...(patch.label !== undefined && { label: patch.label }),
          ...(patch.description !== undefined && { description: patch.description }),
        };
        return n;
      });
      if (patch.label !== undefined && patch.label !== oldLabel) {
        setMappings((prev) =>
          prev.map((m) => ({
            ...m,
            problem_label: m.problem_label === oldLabel ? patch.label! : m.problem_label,
            solution_label: m.solution_label === oldLabel ? patch.label! : m.solution_label,
          })),
        );
      }
    }

    if (patch.linkedProblemLabel !== undefined && currentTag.type === "solution") {
      const solLabel = patch.label !== undefined ? patch.label : currentTag.label;
      setMappings((prev) => {
        const rest = prev.filter((m) => m.solution_label !== currentTag.label && m.solution_label !== solLabel);
        return patch.linkedProblemLabel
          ? [...rest, { problem_label: patch.linkedProblemLabel, solution_label: solLabel }]
          : rest;
      });
    }
  }, [selectedGlobalIndex]);

  return (
    <Dialog open={tagReviewOpen} onOpenChange={setTagReviewOpen}>
      <DialogContent
        showCloseButton
        className="w-full max-w-4xl h-[min(620px,90dvh)] p-0 gap-0 !flex flex-col overflow-hidden border-border sm:max-w-4xl"
      >
        <DialogHeader className="px-5 py-4 shrink-0 border-b border-border space-y-0.5 pr-14 text-left">
          <DialogTitle className="text-base font-semibold">Review Generated Tags</DialogTitle>
          <DialogDescription className="text-xs">
            Filter the list, then edit titles and descriptions. Remove tags you don&apos;t want.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          <TagReviewListPanel
            tags={tags}
            filteredIndices={filteredIndices}
            filter={filter}
            onFilterChange={setFilter}
            selectedGlobalIndex={selectedGlobalIndex}
            onSelect={setSelectedGlobalIndex}
            onRemove={handleRemove}
            invalidIndices={invalidSolutionIndices}
          />
          <TagReviewDetailPanel
            selectionKey={selectedGlobalIndex}
            tag={selectedTag}
            editDraft={editDraft}
            onDraftChange={onDraftChange}
            showLinkedProblem={showLinkedProblem}
            problemOptions={problemOptions}
            onRemove={() => {
              if (selectedGlobalIndex == null) return;
              handleRemove(selectedGlobalIndex);
            }}
          />
        </div>

        <DialogFooter className="px-5 py-3 shrink-0 border-t border-border flex flex-row items-center justify-between gap-3 sm:justify-between">
          <span className="text-xs text-muted-foreground tabular-nums">
            Showing {filteredIndices.length} of {tags.length}
          </span>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setTagReviewOpen(false)}
              disabled={isSavingTags}
            >
              Cancel
            </Button>
            <Button
              className="font-medium"
              onClick={handleSave}
              disabled={isSavingTags || tags.length === 0 || hasEmptyLabels || hasMissingSolutionLinks}
            >
              {isSavingTags ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                `Save ${tags.length} Tags`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
